import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Icon } from "@iconify/react";


export default function EditEventCategoryModel({ open, onClose, categoryId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [iconPreview, setIconPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [initialIsActive, setInitialIsActive] = useState(null);
    const [initialSlug, setInitialSlug] = useState('');
    const [initialName, setInitialName] = useState('');
    const modalRef = useRef(null);
    const prevSlugRef = useRef('');
    const debounceTimer = useRef(null);
    const [imagePreview, setImagePreview] = useState(false);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        editCategory.resetForm();
        setIconPreview(null);
    };

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const response = await axios.get(`${apiUrl}event-service/event-category/${categoryId}`);

                if (response.data.success && response.data.data) {
                    const categories = response.data.data;
                    editCategory.setValues({
                        name: categories.name,
                        icon: categories.icon,
                        isActive: categories.isActive,
                        slug: categories.slug || '',
                    });
                    setInitialIsActive(categories.isActive);
                    setIconPreview(categories.icon?.secure_url);
                    setInitialName(categories.name);
                    setInitialSlug(categories.slug);
                }
            } catch (error) {
                console.error('Error fetching categories data:', error.message);
            }
        };
        fetchCategory();
    }, [categoryId, open]);

    const onSubmit = async (values, { resetForm }) => {
        setIsLoading(true);
        try {
            await validateSlug(values.slug, editCategory.setFieldError, editCategory.setFieldTouched);
            const formData = new FormData();
            if (editCategory.values.icon) {
                formData.append('icon', values.icon);
            }
            formData.append('name', values.name);
            formData.append('slug', values.slug);

            await addFormFieldData(formData);
            resetForm();
            onClose();
            setIsLoading(false);
        } catch (error) {
            console.error('Error submitting form:', error.message);
            setIsLoading(false);
        }
    };

    const validateSlug = async (slug, setFieldError, setFieldTouched) => {
        if (!slug || (slug === initialSlug)) return;
        setFieldTouched("slug", true);
        try {
            const response = await axios.get(`${apiUrl}event-service/event-category/check-slug/${slug}`);
            if (!response.data.success) {
                setFieldError("slug", response.data.message || "Slug is not available");
            }
        } catch (error) {
            if (error.response?.status === 400) {
                setFieldError("slug", error.response.data.message || "Slug already taken");
            } else {
                setFieldError("slug", "Error validating slug. Please try again.");
            }
        }
    };

    const editCategory = useFormik({
        initialValues: {
            name: '',
            icon: '',
            isActive: '',
            slug: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
            slug: Yup.string().required("Slug is required"),
        }),
        onSubmit,
    });

    useEffect(() => {
        const { name } = editCategory.values;
        if (name.trim() && name !== initialName) {
            const slug = name
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '')
                .trim();

            editCategory.setFieldValue('slug', slug);

            if (slug !== prevSlugRef.current) {
                prevSlugRef.current = slug;

                if (debounceTimer.current) clearTimeout(debounceTimer.current);
                debounceTimer.current = setTimeout(() => {
                    validateSlug(slug, editCategory.setFieldError, editCategory.setFieldTouched);
                }, 300);
            }
        }
    }, [editCategory.values.name]);

    const addFormFieldData = async (values) => {
        try {
            const response = await axios.put(`${apiUrl}event-service/event-category/${categoryId}`, values, {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });

            if (response.data.success) {

                if (initialIsActive !== editCategory.values.isActive) {
                    const isActive = editCategory.values.isActive === "true"; // convert string to boolean
                    const action = isActive ? "active" : "deactive";
                    await axios.patch(`${apiUrl}event-service/event-category/${action}/${categoryId}`, null, {
                        headers: { Authorization: `Bearer ${bearerToken}` },
                    });
                }
            }
        } catch (error) {
            console.error('Error updating category:', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImagePreview = (event) => {
        const file = event.currentTarget.files[0];
        editCategory.setFieldValue('icon', file);
        setIconPreview(URL.createObjectURL(file));
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-50" onClick={onClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                {/* Header (Smaller Width, Centered) */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">Edit Event Category</h2>
                </div>

                {/* Form (Starts from Half Header Height) */}
                <div className="relative bg-white p-6 space-y-6 shadow-md rounded-b-lg">
                    <form onSubmit={editCategory.handleSubmit} className="space-y-6">
                        {/* Name and Status Row */}
                        <div className="flex space-x-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Event Category Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Name"
                                    value={editCategory.values.name}
                                    onChange={editCategory.handleChange}
                                    onBlur={editCategory.handleBlur}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {editCategory.touched.name && editCategory.errors.name && (
                                    <div className="text-red-500 text-xs mt-1">{editCategory.errors.name}</div>
                                )}
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    Unique Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="slug"
                                    value={editCategory.values.slug}
                                    onChange={editCategory.handleChange}
                                    onBlur={() => {
                                        if (editCategory.values.slug !== initialSlug) {
                                            validateSlug(editCategory.values.slug, editCategory.setFieldError, editCategory.setFieldTouched);
                                        }
                                    }}
                                    placeholder="unique-name"
                                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                />
                                {editCategory.touched.slug && editCategory.errors.slug && <div className="text-red-500">{editCategory.errors.slug}</div>}
                            </div>
                            <div className="w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    name="isActive"
                                    value={editCategory.values.isActive}
                                    onChange={editCategory.handleChange}
                                    onBlur={editCategory.handleBlur}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                                {editCategory.touched.isActive && editCategory.errors.isActive && (
                                    <div className="text-red-500 text-xs mt-1">{editCategory.errors.isActive}</div>
                                )}
                            </div>
                        </div>

                        {/* Image Upload and Preview Row */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                                    {iconPreview ? (
                                        <img
                                            src={iconPreview}
                                            alt="Icon Preview"
                                            onClick={() => setImagePreview(true)}
                                            className="w-full h-full object-cover rounded-lg cursor-pointer"
                                        />
                                    ) : (
                                        <svg
                                            className="w-6 h-6 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                    )}
                                </div>
                                <div className='bg-[#F8FCFF] p-2'>
                                    <p className="text-xs text-gray-400 mt-1 mb-1 italic">Recommended size: below 1mb, JPG/JPEG/PNG format</p>
                                    <label className="cursor-pointer">
                                        <span className="text-blue-500 hover:text-blue-600 px-4 py-1 border border-blue-500 rounded-md text-sm ">
                                            {iconPreview ? 'Change Image' : 'Choose Image'}
                                        </span>
                                        <input
                                            type="file"
                                            name="icon"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => handleImagePreview(e, editCategory.setFieldValue)}
                                        />
                                    </label>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {isLoading ? 'Updating...' : 'Update Category'}
                            </button>
                        </div>
                    </form>
                    {/* image preview modal */}
                    {imagePreview && (
                        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                            <div className="relative max-w-3xl max-h-[90vh]">
                                <img
                                    src={formImagePreview}
                                    alt="Big Preview"
                                    className="max-h-[90vh] rounded-lg object-contain"
                                />

                                <button
                                    onClick={() => setImagePreview(false)}
                                    className="absolute top-2 right-2 bg-white text-black rounded-full p-2 shadow hover:bg-gray-200"
                                >
                                    <Icon icon="material-symbols-light:close-rounded" className="text-xl" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* loading modal  */}
                    {isLoading && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                                <svg
                                    className="animate-spin h-8 w-8 text-blue-600 mb-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    ></path>
                                </svg>

                                <p className="text-sm font-medium text-gray-700">Saving...</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>

    )
}

EditEventCategoryModel.propTypes = {
    open: PropTypes.isRequired,
    onClose: PropTypes.isRequired,
    categoryId: PropTypes.isRequired,
};